import { type FC, useCallback, useEffect, useState } from 'react';

import ReactDataSheet from 'react-datasheet';
import 'react-datasheet/lib/react-datasheet.css';

import { convertToPercentageStr } from '@soomo/lib/utils/index';

import ColumnOptionsCell from '@editor2/components/DataTable/ColumnOptionsCell';
import { type GridElement } from '@editor2/types/gridElement';
import { type PollChoice } from '@editor2/types/pageElements';
import { getColumnSum, updateArrayAtIndex } from './helpers';
import { pollDataTable } from './styles';
import { type PollDataType } from './types';

class MyReactDataSheet extends ReactDataSheet<GridElement, any> {}

interface Props {
	data: (string[] | number[])[];
	total_count: number[];
	data_type: PollDataType;
	contextual_info: string[];
	choices: PollChoice[];
	onChange: (_) => void;
}

const PollDataTable: FC<Props> = (props) => {
	const {
		data,
		data_type: dataType,
		total_count: totalCount,
		contextual_info: contextualInfo,
		choices,
		onChange
	} = props;

	const [grid, setGrid] = useState([]);

	const generateNumberRow = useCallback(
		(row: number[], index: number) => {
			const responses = choices.map((choice) => choice.body);
			const responseCell = { value: responses[index], readOnly: true };
			const dataRow = row.flatMap((item, index) => {
				const percentage = convertToPercentageStr(Number(item), Number(totalCount[index]), 1);
				return [{ value: item }, { value: percentage, expr: percentage, readOnly: true }];
			});

			return [responseCell, ...dataRow];
		},
		[choices, totalCount]
	);

	const generatePercentageRow = useCallback(
		(row: number[], index: number) => {
			const responses = choices.map((choice) => choice.body);
			const responseCell = { value: responses[index], readOnly: true };
			const dataRow = row.flatMap((item, index) => {
				return [
					{
						value: totalCount[index] ? (totalCount[index] * item) / 100 : '-',
						readOnly: true
					},
					{ value: `${item} %`, expr: item }
				];
			});

			return [responseCell, ...dataRow];
		},
		[choices, totalCount]
	);

	const generateDataRows = useCallback(() => {
		const rowsData = data.slice(1);
		switch (dataType) {
			case 'number':
				return rowsData.map(generateNumberRow);
			case 'percentage':
				return rowsData.map(generatePercentageRow);
			default:
				break;
		}
	}, [data, dataType, generateNumberRow, generatePercentageRow]);

	const generateTotalNumberRow = (item: number) => {
		return [
			{ value: item, readOnly: true },
			{ value: '100%', readOnly: true }
		];
	};

	const generateTotalPercentageRow = useCallback(
		(item: number, index: number) => [
			{ value: item },
			{ value: `${getColumnSum(data.slice(1) as number[][], index)} %`, readOnly: true }
		],
		[data]
	);

	const generateTotalRow = useCallback(() => {
		let row = [];
		switch (dataType) {
			case 'number':
				row = totalCount.flatMap(generateTotalNumberRow);
				break;
			case 'percentage':
				row = totalCount.flatMap(generateTotalPercentageRow);
				break;
			default:
				break;
		}

		return [{ value: 'Total', readOnly: true }, ...row];
	}, [dataType, totalCount, generateTotalPercentageRow]);

	const onAddColumnClick = useCallback(
		(index: number) => {
			const updatedData = data.map((row) => [...row.slice(0, index), '', ...row.slice(index)]);
			const updatedTotal = [...totalCount.slice(0, index), 0, ...totalCount.slice(index)];
			const updatedContextualInfo = [
				...contextualInfo.slice(0, index),
				'',
				...contextualInfo.slice(index)
			];
			onChange({
				data: updatedData,
				total_count: updatedTotal,
				contextual_info: updatedContextualInfo
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contextualInfo, data, totalCount]
	);

	const onDeleteColumnClick = useCallback(
		(index: number) => {
			const updatedData = data.map((row) => [...row.slice(0, index), ...row.slice(index + 1)]);
			const updatedTotal = [...totalCount.slice(0, index), ...totalCount.slice(index + 1)];
			const updatedContextualInfo = [
				...contextualInfo.slice(0, index),
				...contextualInfo.slice(index + 1)
			];
			onChange({
				data: updatedData,
				total_count: updatedTotal,
				contextual_info: updatedContextualInfo
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contextualInfo, data, totalCount]
	);

	const generateHeaderRow = useCallback(() => {
		const headerRow = data[0].map((item, index) => ({
			value: (
				<ColumnOptionsCell
					label={item}
					index={index}
					onAddColumnClick={onAddColumnClick}
					onDeleteColumnClick={onDeleteColumnClick}
				/>
			),
			expr: item,
			colSpan: 2
		}));

		return [{ value: 'Dataset title', readOnly: true }, ...headerRow];
	}, [data, onAddColumnClick, onDeleteColumnClick]);

	const generateGrid = useCallback(() => {
		const headerRow = generateHeaderRow();
		const dataRows = generateDataRows();
		const totalRow = generateTotalRow();

		return [headerRow, ...dataRows, totalRow];
	}, [generateHeaderRow, generateDataRows, generateTotalRow]);

	useEffect(() => {
		const rows = generateGrid();
		setGrid(rows);
	}, [generateGrid]);

	const onCellsChanged = (changes) => {
		let updatedData = [...data];
		let updatedTotalRow = [...totalCount];

		changes.forEach(({ row, col, value }) => {
			if (row === data.length) {
				updatedTotalRow = updateArrayAtIndex(updatedTotalRow, Math.floor(col / 2), value);
			} else if (row === 0) {
				const updatedRow = updateArrayAtIndex(updatedData[row], col - 1, value);
				updatedData = updateArrayAtIndex(updatedData, row, updatedRow);
			} else {
				const colIndex = dataType === 'number' ? Math.floor(col / 2) : Math.floor(col / 2) - 1;

				const updatedRow = updateArrayAtIndex(updatedData[row], colIndex, value);
				updatedData = updateArrayAtIndex(updatedData, row, updatedRow);

				if (dataType === 'number') {
					const total = getColumnSum(updatedData.slice(1) as number[][], colIndex);
					updatedTotalRow = updateArrayAtIndex(updatedTotalRow, colIndex, total);
				}
			}
		});

		onChange({ data: updatedData, total_count: updatedTotalRow });
	};

	return (
		<MyReactDataSheet
			className={`${pollDataTable}`}
			data={grid}
			valueRenderer={(cell) => cell.value}
			dataRenderer={(cell) => cell.expr}
			onCellsChanged={onCellsChanged}
		/>
	);
};

export default PollDataTable;
